import * as iltypes from "src/interfaces/InleagueApiV1"
import * as ilapi from "src/composables/InleagueApiV1"
import { type AxiosInstance } from "axios";
import { maybeParseJSON } from "src/helpers/utils";

import { User } from "src/store/User";
import { Type, type Static } from "@sinclair/typebox"
import { Value } from '@sinclair/typebox/value'
import { ref } from "vue";

/**
 * type of a objects as we expect to deal with on this page
 * At runtime, if it's not expanded in this way, retrieval was wrong
 */
export type ExpandedCompetitionRegistration = iltypes.WithDefinite<iltypes.CompetitionRegistration, "competitionSeason">
export type ExpandedRegistration = iltypes.Registration & {competitions: ExpandedCompetitionRegistration[]};
export type ExpandedChild = iltypes.Child & {
  mostRecentRegistration: ExpandedRegistration,
  permLeagueComment: Exclude<iltypes.Child["permLeagueComment"], undefined>
}

// todo: tight return type with some WithDefinite or similar
export async function getExpandedRegistration(axios: AxiosInstance, registrationID: iltypes.Guid) : Promise<iltypes.Registration> {
  return await ilapi.getRegistration(
    axios, {
      registrationID: registrationID,
      expand: [
        "parent1ID",
        "parent2ID",
        "competitions.competitionSeason",
        "leagueComment",
        "player",
        "player.permLeagueComment", // this is not expanded in getPlayerDetails, is it auto-expanded?
        "registrationAnswers",
        "transactions",
        "lastModifiedByUser",
        "contactPhone",
        "contactName",
        "contact2Phone",
        "contact2Name",
        "photoURL"
      ]});
}

export const TabID_t = {
  'registration-data': 'registration-data',
  'ratings-history': 'ratings-history',
  'player-loan': 'player-loan',
  'scholarship-data': 'scholarship-data',
  'player-logs': 'player-logs',
  'player-merge': 'player-merge',
} as const;
export type TabID_t = (typeof TabID_t)[keyof typeof TabID_t];



export type PlayerEditorElementExpansionStore = ReturnType<typeof PlayerEditorElementExpansionStore>
export function PlayerEditorElementExpansionStore() {
  const key = () => `PlayerEditorElementExpansionStore/${User.userData?.userID ?? "<nil>"}`
  const schema = () => {
    return Type.Object({
      version: Type.Number(),
      transactions: Type.Boolean(),
      playerData: Type.Boolean(),
      parent1Data: Type.Boolean(),
      parent2Data: Type.Boolean(),
      registrationData: Type.Boolean(),
      medicalInformation: Type.Boolean(),
      emergencyContact: Type.Boolean(),
      customQuestions: Type.Boolean(),
      programRegistrations: Type.Boolean(),
    })
  }
  type Data = Static<ReturnType<typeof schema>>

  const defaultData = () : Data => {
    return {
      version: 1,
      transactions: true,
      playerData: true,
      parent1Data: true,
      parent2Data: true,
      registrationData: true,
      medicalInformation: true,
      emergencyContact: true,
      customQuestions: true,
      programRegistrations: true,
    }
  }

  const data = ref<Data>(defaultData())

  const load = () : void => {
    const s = localStorage.getItem(key())
    if (!s) {
      data.value = defaultData()
      return
    }

    const m = maybeParseJSON(s)
    if (!m) {
      data.value = defaultData()
    }

    if (Value.Check(schema(), m)) {
      data.value = m
    }
    else {
      data.value = defaultData()
    }
  }

  const store = () : void => {
    localStorage.setItem(key(), JSON.stringify(data.value))
  }

  return {
    load,
    get transactions() { return data.value.transactions },
    set transactions(v) {
      data.value.transactions = v
      store()
    },
    get playerData() { return data.value.playerData },
    set playerData(v) {
      data.value.playerData = v
      store()
    },
    get parent1Data() { return data.value.parent1Data },
    set parent1Data(v) {
      data.value.parent1Data = v
      store()
    },
    get parent2Data() { return data.value.parent2Data },
    set parent2Data(v) {
      data.value.parent2Data = v
      store()
    },
    get registrationData() { return data.value.registrationData },
    set registrationData(v) {
      data.value.registrationData = v
      store()
    },
    get medicalInformation() { return data.value.medicalInformation },
    set medicalInformation(v) {
      data.value.medicalInformation = v
      store()
    },
    get emergencyContact() { return data.value.emergencyContact },
    set emergencyContact(v) {
      data.value.emergencyContact = v
      store()
    },
    get customQuestions() { return data.value.customQuestions },
    set customQuestions(v) {
      data.value.customQuestions = v
      store()
    },
    get programRegistrations() { return data.value.programRegistrations },
    set programRegistrations(v) {
      data.value.programRegistrations = v
      store()
    },
  }
}
